import React, { useState } from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Card, CardContent, Divider, Grid } from '@mui/material';
import { useForm } from 'react-hook-form';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { GetNonServerErrorMessage, colours } from 'utils/constants';
import PrimaryButton from 'components/elements/Form/button/primaryButton';
import InputField from 'components/elements/Form/Input/InputField';
import useSaveAdmin from 'components/structure/Admin/AdminForms/crud/useSave';
import { toast } from 'react-toastify';
import AdminService, { AdminProps, UpdateEmailProps } from 'services/hooks/admin-service';

/* The code is defining a validation schema using the `yup` library. If these validation rules are not met, an error message will be displayed. */
const ResetEmailSchema = yup.object().shape({
  username: yup.string().email('Invalid email format').required('Email address is required')
});

const ResetEmailForm = ({ onBack, adminData }: { onBack: () => void; adminData: AdminProps }) => {
  const [disabled, setDisabled] = useState(false);

  /* The `useForm` hook from the `react-hook-form` library is being used to create a form with form validation. */
  const methods = useForm({
    defaultValues: {
      username: ''
    },
    resolver: yupResolver(ResetEmailSchema)
  });

  /* The code is creating a mutation function called `saveAdminMutation` using the `useSaveAdmin` hook. This hook takes in 
  several parameters: */
  const saveAdminMutation = useSaveAdmin({
    createQuery: AdminService.createAdmin,
    updateQuery: AdminService.updateAdmin,
    queryName: 'admins',
    onSuccess: () => {
      onBack();
      toast.success('Email address updated successfully.');
      setDisabled(false);
    },
    onError: (error: any) => {
      if (error.response) {
        const errorMessage =
          error.response.data?.error ||
          error.response.data?.message ||
          'An unexpected error occurred.';

        switch (error.response.status) {
          case 409: // Conflict, user with the provided email already exists
            toast.error(errorMessage, { autoClose: 15000 });
            break;
          default:
            toast.error('Failed to update email address. Please try again.');
            break;
        }
      } else {
        // Handle cases where the error is not from the server response
        toast.error(GetNonServerErrorMessage);
      }
      setDisabled(false);
    }
  });

  /* The `onSubmit` function takes an `UpdateEmailProps` object as an argument, extracts specific property from it, and passes them to the 
  `saveAdminMutation.mutate` function. */
  const onSubmit = async (e: UpdateEmailProps) => {
    setDisabled(true);
    if (adminData) {
      saveAdminMutation.mutate({
        uuid: adminData?.uuid,
        username: e.username,
        password: adminData?.password,
        role: adminData?.role,
        accountUuid: adminData?.accountUuid,
        buildingUuid: adminData?.buildingUuid,
        organizationUuid: adminData?.organizationUuid
      });
    }
  };

  return (
    <form onSubmit={methods.handleSubmit(onSubmit)} className={`text-left items-start`}>
      <div>
        <Card variant="outlined" sx={{ width: '100%', borderRadius: '8px' }}>
          <CardContent>
            <Grid item xs={1}>
              <Button
                color="primary"
                fullWidth
                onClick={onBack}
                sx={{
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  color: 'black',
                  fontSize: '1rem'
                }}
              >
                <ArrowBackIosIcon sx={{ width: '15px', color: colours.seafoam }} />
                Back
              </Button>
            </Grid>
            <Divider variant="middle" style={{ margin: '20px 0 40px' }} />
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <InputField
                  label="Type your new email address*"
                  name="username"
                  type="email"
                  placeholder="sam.smith@b-line.io"
                  autoFocus
                  methods={methods}
                  autoComplete="new-password"
                />
              </Grid>
              <Grid item xs={4}>
                <PrimaryButton
                  buttonLabel="Save Email"
                  variant="default"
                  size={'mt-4 px-6 w-auto gap-2'}
                  disabled={disabled}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </div>
    </form>
  );
};

export default ResetEmailForm;
